import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, usePermissions, useTranslate } from 'react-admin';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Paper,
  Typography,
  Alert,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Chip,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import ComputerIcon from '@mui/icons-material/Computer';
import SendIcon from '@mui/icons-material/Send';
import { getEventsV2 } from '../services/eventsV2Service';
import {
  getPushStats,
  searchLegacyPushUsers,
  searchNewPushUsers,
  searchUsersForNotify,
  sendPushNotification,
  sendWebNotification,
} from '../services/pushNotificationsService';
import { CROLE_DEFINITIONS } from '../constants/croles';

const ROLE_OPTIONS = [
  { id: 'user', name: 'Site kullanıcısı (user)' },
  ...CROLE_DEFINITIONS,
  { id: 'affiliate', name: 'Affiliate' },
  { id: 'salePointManager', name: 'Sale Point Manager' },
];

/** 0 = eski mobil, 1 = yeni mobil, 2 = web */
const TAB_LEGACY = 0;
const TAB_NEW = 1;
const TAB_WEB = 2;

const userLabel = (u) => {
  if (!u) return '';
  const name = [u.name, u.surname].filter(Boolean).join(' ').trim();
  const email = u.email || '';
  const tokens =
    typeof u.tokenCount === 'number' && u.tokenCount > 0
      ? ` · ${u.tokenCount} token`
      : '';
  if (name && email) return `${name} · ${email}${tokens}`;
  return `${name || email || u.id || ''}${tokens}`;
};

const PushNotificationsPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { permissions } = usePermissions();

  const [tab, setTab] = useState(TAB_LEGACY);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [legacyTokenCount, setLegacyTokenCount] = useState(null);
  const [newTokenCount, setNewTokenCount] = useState(null);
  const [webSubscriptionCount, setWebSubscriptionCount] = useState(null);
  const [webOnlineUsers, setWebOnlineUsers] = useState(null);
  const [webLoggedInUsers, setWebLoggedInUsers] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Hedef kitle (mobil + web)
  const [audience, setAudience] = useState('all');
  const [selectedRole, setSelectedRole] = useState('user');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchInput, setUserSearchInput] = useState('');

  const isLegacyTab = tab === TAB_LEGACY;
  const isNewTab = tab === TAB_NEW;
  const isWebTab = tab === TAB_WEB;
  const isMobileTab = isLegacyTab || isNewTab;

  const loadMeta = useCallback(
    async (opts = {}) => {
      const silent = opts.silent === true;
      if (!permissions) return;
      if (!silent) setLoadingMeta(true);
      try {
        const [stats, upcomingEvents] = await Promise.all([
          getPushStats(),
          isWebTab || !silent
            ? getEventsV2(permissions, { limitCount: 200 })
            : Promise.resolve(null),
        ]);

        const legacy =
          typeof stats?.legacyTokenCount === 'number'
            ? stats.legacyTokenCount
            : typeof stats?.tokenCount === 'number'
              ? stats.tokenCount
              : 0;
        const neu =
          typeof stats?.newAppTokenCount === 'number'
            ? stats.newAppTokenCount
            : 0;

        setLegacyTokenCount(legacy);
        setNewTokenCount(neu);
        setWebSubscriptionCount(
          typeof stats?.webSubscriptionCount === 'number'
            ? stats.webSubscriptionCount
            : 0,
        );
        setWebOnlineUsers(
          typeof stats?.webOnlineUsers === 'number' ? stats.webOnlineUsers : 0,
        );
        setWebLoggedInUsers(
          typeof stats?.webLoggedInUsers === 'number'
            ? stats.webLoggedInUsers
            : 0,
        );

        if (!silent && upcomingEvents != null) {
          setEvents(
            Array.isArray(upcomingEvents)
              ? upcomingEvents
              : upcomingEvents?.data ?? [],
          );
        }
      } catch (err) {
        if (!silent) {
          notify(err?.message || 'Veriler yüklenemedi', { type: 'warning' });
          setLegacyTokenCount(0);
          setNewTokenCount(0);
          setWebSubscriptionCount(0);
          setWebLoggedInUsers(0);
          setEvents([]);
        }
        setWebOnlineUsers(0);
      } finally {
        if (!silent) setLoadingMeta(false);
      }
    },
    [permissions, notify, isWebTab],
  );

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (!isWebTab || !permissions) return undefined;
    const t = setInterval(() => loadMeta({ silent: true }), 3000);
    return () => clearInterval(t);
  }, [isWebTab, permissions, loadMeta]);

  // Tab degisince hedefi sifirla
  useEffect(() => {
    setAudience(isWebTab ? 'live' : 'all');
    setSelectedUsers([]);
    setUserOptions([]);
    setUserSearchInput('');
    setResult(null);
    setSelectedEvent(null);
  }, [tab, isWebTab]);

  useEffect(() => {
    if (audience !== 'users') return undefined;
    const q = userSearchInput.trim();
    if (q.length < 2) {
      setUserOptions([]);
      return undefined;
    }
    let cancelled = false;
    setUserSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        let rows = [];
        if (isLegacyTab) {
          // Sadece tokeni olanlar — genel user listesi YOK
          rows = await searchLegacyPushUsers(q);
        } else if (isNewTab) {
          rows = await searchNewPushUsers(q);
        } else {
          rows = await searchUsersForNotify(q);
        }
        if (!cancelled) setUserOptions(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (!cancelled) {
          setUserOptions([]);
          notify(err?.message || 'Kullanıcı araması başarısız', {
            type: 'warning',
          });
        }
      } finally {
        if (!cancelled) setUserSearchLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    audience,
    userSearchInput,
    isLegacyTab,
    isNewTab,
    isWebTab,
    notify,
  ]);

  const mobileTokenCount = isLegacyTab
    ? legacyTokenCount
    : isNewTab
      ? newTokenCount
      : 0;

  const audienceReady = useMemo(() => {
    if (audience === 'all' || audience === 'live') return true;
    if (audience === 'role') return Boolean(selectedRole);
    if (audience === 'users') return selectedUsers.length > 0;
    return false;
  }, [audience, selectedRole, selectedUsers]);

  const canSend =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    !sending &&
    audienceReady;

  const mobileDisabled = !canSend || (mobileTokenCount ?? 0) === 0;
  const webDisabled =
    !canSend || (audience === 'live' && (webOnlineUsers ?? 0) === 0);
  const buttonDisabled = isWebTab ? webDisabled : mobileDisabled;

  const handleSend = async () => {
    if (!canSend) return;

    if (isWebTab) {
      let targetLabel = 'login olmuş herkese';
      if (audience === 'live') {
        targetLabel = `sadece canlı WS (${webOnlineUsers ?? 0} sekme)`;
      } else if (audience === 'role') {
        const roleName =
          ROLE_OPTIONS.find((r) => r.id === selectedRole)?.name || selectedRole;
        targetLabel = `rol: ${roleName}`;
      } else if (audience === 'users') {
        targetLabel = `${selectedUsers.length} kullanıcı`;
      }
      if (
        !window.confirm(
          `"${title.trim()}" web bildirimi → ${targetLabel}. Devam?`,
        )
      ) {
        return;
      }
    } else {
      const appLabel = isLegacyTab ? 'eski mobil' : 'yeni mobil';
      const who =
        audience === 'users'
          ? `${selectedUsers.length} kullanıcı`
          : `tüm tokenlar (${mobileTokenCount ?? 0})`;
      if (
        !window.confirm(
          `"${title.trim()}" ${appLabel} → ${who}. Devam?`,
        )
      ) {
        return;
      }
    }

    setSending(true);
    setResult(null);
    try {
      if (isWebTab) {
        const res = await sendWebNotification({
          title: title.trim(),
          body: body.trim(),
          eventId: selectedEvent?.id || undefined,
          audience,
          ...(audience === 'role' ? { crole: selectedRole } : {}),
          ...(audience === 'users'
            ? { userIds: selectedUsers.map((u) => u.id) }
            : {}),
        });
        setResult(res);
        const pushN = Number(res?.webPushSent) || 0;
        const liveN = Number(res?.webOnlineReached) || 0;
        const targets = Number(res?.targetUserCount) || 0;
        if (targets > 0 || pushN > 0 || liveN > 0) {
          notify(
            `Web gönderildi · hedef ${targets} · canlı ${liveN} · OS push ${pushN}`,
            { type: 'success' },
          );
        } else {
          notify(res?.message || 'Hedef yok', { type: 'warning' });
        }
      } else {
        const res = await sendPushNotification({
          title: title.trim(),
          body: body.trim(),
          app: isLegacyTab ? 'legacy' : 'new',
          audience: audience === 'users' ? 'users' : 'all',
          ...(audience === 'users'
            ? { userIds: selectedUsers.map((u) => u.id) }
            : {}),
        });
        setResult(res);
        if ((res?.sent ?? 0) > 0) {
          notify(
            `${isLegacyTab ? 'Eski' : 'Yeni'} mobil gönderildi (${res.sent} cihaz)`,
            { type: 'success' },
          );
        } else {
          notify(res?.message || 'Token yok / gönderilemedi', {
            type: 'warning',
          });
        }
      }

      setTitle('');
      setBody('');
      setSelectedEvent(null);
      setSelectedUsers([]);
      await loadMeta();
    } catch (err) {
      notify(err?.message || 'Gönderim başarısız', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <NotificationsActiveIcon color="primary" fontSize="large" />
        <Box>
          <Typography variant="h4" component="h1">
            {translate('notifications.title', { _: 'Bildirimler' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('notifications.desc', { _: 'Eski mobil (Gişe Expo), yeni mobil ve web ayrı kanallar.' })}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="fullWidth"
        >
          <Tab
            icon={<PhoneAndroidIcon />}
            iconPosition="start"
            label={translate('notifications.tab_legacy_mobile', { _: 'Eski mobil uygulama' })}
          />
          <Tab
            icon={<PhoneIphoneIcon />}
            iconPosition="start"
            label={translate('notifications.tab_new_mobile', { _: 'Yeni mobil uygulama' })}
          />
          <Tab
            icon={<ComputerIcon />}
            iconPosition="start"
            label={translate('notifications.tab_web', { _: 'Web Bildirimi' })}
          />
        </Tabs>
      </Paper>

      <Paper sx={{ p: 3 }}>
        {loadingMeta ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              {isLegacyTab && (
                <>
                  {translate('notifications.legacy_token_info', { _: 'Eski Gişe Expo token:' })}{' '}
                  <strong>{legacyTokenCount ?? 0}</strong>
                  <Typography
                    component="span"
                    variant="body2"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {translate('notifications.legacy_token_desc', { _: 'Firebase `users.token` → legacy_push_tokens. Sadece konu + açıklama. İstersen token’lı kullanıcı seç.' })}
                  </Typography>
                </>
              )}
              {isNewTab && (
                <>
                  {translate('notifications.new_token_info', { _: 'Yeni mobil token:' })}{' '}
                  <strong>{newTokenCount ?? 0}</strong>
                  <Typography
                    component="span"
                    variant="body2"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {translate('notifications.new_token_desc', { _: 'Nest `push_tokens` (yeni app register). Sadece konu + açıklama. İstersen token’lı kullanıcı seç.' })}
                  </Typography>
                </>
              )}
              {isWebTab && (
                <>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                    <Typography variant="body2">
                      {translate('notifications.web_live_ws', { _: 'Canlı WS:' })}{' '}
                      <strong
                        style={{
                          color:
                            (webOnlineUsers ?? 0) > 0 ? '#16a34a' : undefined,
                        }}
                      >
                        {webOnlineUsers ?? 0}
                      </strong>
                    </Typography>
                    <Typography variant="body2">
                      {translate('notifications.web_logged_in', { _: 'Login olmuş:' })}{' '}
                      <strong>{webLoggedInUsers ?? 0}</strong>
                    </Typography>
                    <Typography variant="body2">
                      {translate('notifications.web_os_push', { _: 'OS push:' })}{' '}
                      <strong>{webSubscriptionCount ?? 0}</strong>
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {translate('notifications.web_live_desc', { _: 'Canlı = sitede açık sekme + WebSocket. Her 3 sn yenilenir.' })}
                  </Typography>
                </>
              )}
            </Alert>

            {/* Mobil hedef */}
            {isMobileTab && (
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend">
                  {translate('notifications.target_mobile', { _: 'Hedef (mobil)' })}
                </FormLabel>
                <RadioGroup
                  row
                  value={audience}
                  onChange={(e) => {
                    setAudience(e.target.value);
                    setSelectedUsers([]);
                    setUserSearchInput('');
                    setUserOptions([]);
                  }}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio size="small" />}
                    label={translate('notifications.target_all_tokens', { _: 'Tüm token’lara' })}
                  />
                  <FormControlLabel
                    value="users"
                    control={<Radio size="small" />}
                    label={translate('notifications.target_select_users_token', { _: 'Kullanıcı seç (token’lı)' })}
                  />
                </RadioGroup>
              </FormControl>
            )}

            {/* Web hedef */}
            {isWebTab && (
              <>
                <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                  <FormLabel component="legend">
                    {translate('notifications.target_web', { _: 'Hedef kitle (web)' })}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                  >
                    <FormControlLabel
                      value="live"
                      control={<Radio size="small" />}
                      label={translate('notifications.target_live_only', { _: 'Sadece canlı (WS)' })}
                    />
                    <FormControlLabel
                      value="all"
                      control={<Radio size="small" />}
                      label={translate('notifications.target_all_logged_in', { _: 'Login olmuş herkese' })}
                    />
                    <FormControlLabel
                      value="role"
                      control={<Radio size="small" />}
                      label={translate('notifications.target_by_role', { _: 'Role göre' })}
                    />
                    <FormControlLabel
                      value="users"
                      control={<Radio size="small" />}
                      label={translate('notifications.target_select_users', { _: 'Kullanıcı seç' })}
                    />
                  </RadioGroup>
                </FormControl>

                {audience === 'role' && (
                  <TextField
                    select
                    label={translate('notifications.role_label', { _: 'Rol' })}
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    fullWidth
                    margin="normal"
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </>
            )}

            {audience === 'users' && (
              <Autocomplete
                multiple
                sx={{ mt: 1, mb: 1 }}
                options={userOptions}
                value={selectedUsers}
                onChange={(_e, value) => setSelectedUsers(value)}
                inputValue={userSearchInput}
                onInputChange={(_e, value, reason) => {
                  if (reason === 'reset') return;
                  setUserSearchInput(value);
                }}
                getOptionLabel={userLabel}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                loading={userSearchLoading}
                filterOptions={(x) => x}
                noOptionsText={
                  userSearchLoading
                    ? translate('notifications.searching', { _: 'Aranıyor...' })
                    : userSearchInput.trim().length < 2
                      ? translate('notifications.min_chars', { _: 'En az 2 karakter yazın' })
                      : isMobileTab
                        ? translate('notifications.no_user_with_token', { _: 'Token’ı olan kullanıcı yok' })
                        : translate('notifications.no_user_found', { _: 'Kullanıcı bulunamadı' })
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={userLabel(option)}
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      isMobileTab
                        ? translate('notifications.search_user_with_token', { _: 'Token’ı olan kullanıcı ara' })
                        : translate('notifications.search_user', { _: 'Kullanıcı ara' })
                    }
                    placeholder={translate('notifications.search_placeholder', { _: 'E-posta, ad, soyad...' })}
                    helperText={
                      isMobileTab
                        ? translate('notifications.mobile_token_help', { _: 'Sadece bu app’te push token’ı olanlar listelenir' })
                        : translate('notifications.web_users_help', { _: 'Seçilen kullanıcılara gider' })
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userSearchLoading ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            <TextField
              label={translate('notifications.subject', { _: 'Konu' })}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              margin="normal"
              inputProps={{ maxLength: 120 }}
            />

            <TextField
              label={translate('notifications.description', { _: 'Açıklama' })}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
              required
              margin="normal"
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
            />

            {/* Etkinlik sadece web */}
            {isWebTab && (
              <Autocomplete
                sx={{ mt: 2, mb: 1 }}
                options={events}
                value={selectedEvent}
                onChange={(_e, value) => setSelectedEvent(value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate('notifications.event_optional', { _: 'Etkinlik (opsiyonel)' })}
                    helperText={translate('notifications.event_optional_help', { _: 'Tıklanınca etkinlik sayfası açılır' })}
                  />
                )}
              />
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  sending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
                disabled={buttonDisabled}
                onClick={handleSend}
              >
                {isLegacyTab && translate('notifications.send_legacy_mobile', { _: 'Eski mobile gönder' })}
                {isNewTab && translate('notifications.send_new_mobile', { _: 'Yeni mobile gönder' })}
                {isWebTab && translate('notifications.send_web', { _: 'Web’e Gönder' })}
              </Button>
            </Box>

            {result && (
              <Alert
                severity={
                  isWebTab
                    ? result.targetUserCount ||
                      result.webPushSent ||
                      result.webReached
                      ? 'success'
                      : 'warning'
                    : result.sent
                      ? 'success'
                      : 'warning'
                }
                sx={{ mt: 3 }}
              >
                {isWebTab ? (
                  <>
                    Hedef: {result.targetUserCount ?? 0}
                    {' · '}
                    OS push: {result.webPushSent ?? 0}
                    {' · '}
                    Canlı: {result.webOnlineReached ?? 0}
                    {result.message ? ` · ${result.message}` : ''}
                  </>
                ) : (
                  <>
                    App: {result.app ?? (isLegacyTab ? 'legacy' : 'new')}
                    {' · '}
                    Token: {result.tokenCount ?? 0}
                    {' · '}
                    Push: {result.sent ?? 0}
                    {result.skipped ? ` · Atlanan: ${result.skipped}` : ''}
                    {result.message ? ` · ${result.message}` : ''}
                  </>
                )}
              </Alert>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default PushNotificationsPage;
